import { Link } from "react-router-dom";
import { Plus, Tag, Check } from "lucide-react";
import { useCart } from "../lib/cart";

// Build slot tiles from cart lines whose variant matches the deal id.
function eligibleSlots(items, dealId, current) {
    const thumbs = [];
    for (const it of items || []) {
        const variant =
            (it.product?.variants || []).find((v) => v.option === it.option) ||
            (it.product?.variants || [])[0];
        if (!variant) continue;
        const ids = new Set([
            ...(variant.specialConditionIds || []),
            ...(variant.specialRewardIds || []),
        ]);
        if (!ids.has(dealId)) continue;
        const img = it.product?.images?.[0]?.url;
        const label = variant.option || "";
        for (let i = 0; i < (it.quantity || 0); i++) {
            thumbs.push({ img, label, filled: true, key: `${it.id}-${i}` });
        }
    }
    return thumbs.slice(0, Math.max(0, current));
}

/**
 * Cart deal-progress widget. Pale-olive panel with:
 *  - Deal title (e.g. "BUY 2, GET 1 FREE")
 *  - Slot grid: filled tiles show product thumbnails, empty tiles show "+"
 *  - Radial-progress tag icon + "Add N more..." + "View Eligible Items"
 *
 * Designed to surface multiple deal progress cards in a stacked column inside
 * CartDrawer so customers see every offer they're close to unlocking.
 */
export default function CartDealCard({ progress, items, storeSlug }) {
    const { setOpen: setCartOpen } = useCart();
    if (!progress) return null;
    const threshold = Math.max(1, progress.threshold || 1);
    const current = Math.min(threshold, progress.current || 0);
    const needed = Math.max(0, threshold - current);
    const pct = Math.min(100, (current / threshold) * 100);

    // Radial progress on r=14 → circumference = 2πr ≈ 87.96
    const radius = 14;
    const circumference = 2 * Math.PI * radius;
    const dashoffset = circumference - (pct / 100) * circumference;

    const title = progress.short || progress.name;
    const filled = eligibleSlots(items, progress.id, current);
    // Compact display: show slot grid for thresholds up to 6 so BOGO-style
    // deals get a visible product thumbnail. For very large thresholds (>6)
    // we still drop the grid so the cart drawer stays dominated by the
    // customer's actual line items.
    const showSlotGrid = threshold <= 6;

    return (
        <div
            data-testid="cart-deal-card"
            data-deal-id={progress.id}
            className="bg-zen-olive/10 border border-zen-olive/30 px-3 py-2.5"
        >
            <div className="flex items-center gap-3">
                {/* Progress meter — always visible, tight. */}
                <div className="relative shrink-0 w-8 h-8">
                    <svg viewBox="0 0 36 36" className="w-8 h-8 -rotate-90" aria-hidden="true">
                        <circle
                            cx="18"
                            cy="18"
                            r={radius}
                            fill="none"
                            stroke="rgba(107,124,79,0.25)"
                            strokeWidth="3"
                        />
                        <circle
                            cx="18"
                            cy="18"
                            r={radius}
                            fill="none"
                            stroke="#6B7C4F"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeDasharray={circumference}
                            strokeDashoffset={dashoffset}
                            className="transition-[stroke-dashoffset] duration-500"
                        />
                    </svg>
                    <Tag
                        size={11}
                        strokeWidth={2}
                        className="absolute inset-0 m-auto text-zen-olive-dark"
                    />
                </div>
                {/* Title + microcopy stack — title in two lines max, copy below. */}
                <div className="flex-1 min-w-0 leading-tight">
                    <p
                        data-testid="cart-deal-name"
                        className="font-semibold text-zen-ink text-[11px] sm:text-xs leading-tight tracking-wide uppercase line-clamp-2"
                        title={title}
                    >
                        {title}
                    </p>
                    <p
                        data-testid="cart-deal-needed"
                        className="text-[11px] text-zen-ink-soft leading-snug mt-0.5"
                    >
                        {needed > 0
                            ? `Add ${needed} more.`
                            : "Offer ready — applied at checkout."}
                        {storeSlug && progress.id ? (
                            <>
                                {" "}
                                <Link
                                    to={`/menu/${storeSlug}/special/${progress.id}`}
                                    onClick={() => setCartOpen(false)}
                                    data-testid="cart-deal-view-eligible"
                                    className="text-zen-olive-dark hover:text-zen-olive underline underline-offset-2 font-medium"
                                >
                                    View offer
                                </Link>
                            </>
                        ) : null}
                    </p>
                </div>
            </div>

            {/* Slot row — 48×48 chips so the product image is actually
                visible (BOGO offers in particular benefit from showing what
                you already have on the deal). Single row, scrolls
                horizontally if thresholds get wide. */}
            {showSlotGrid && (
                <div className="flex gap-2 mt-2.5 ml-11 overflow-x-auto pb-0.5">
                    {Array.from({ length: threshold }).map((_, i) => {
                        const slot = filled[i];
                        return (
                            <div
                                key={`slot-${progress.id}-${i}`}
                                data-testid={`cart-deal-slot-${i}`}
                                data-filled={slot ? "true" : "false"}
                                className={`relative shrink-0 w-12 h-12 bg-zen-bg border ${
                                    slot
                                        ? "border-zen-olive/40"
                                        : "border-zen-olive/30 border-dashed"
                                } flex items-center justify-center overflow-hidden`}
                            >
                                {slot ? (
                                    slot.img ? (
                                        <img
                                            src={slot.img}
                                            alt=""
                                            loading="lazy"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <Check size={16} className="text-zen-olive" />
                                    )
                                ) : (
                                    <Plus
                                        size={16}
                                        strokeWidth={1.5}
                                        className="text-zen-olive/50"
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
